import { useState } from "react";
import { useAccountContext } from "./useAccountContext";
import { useAuthContext } from "./useAuthContext";
import { useSettingsContext } from "./useSettingsContext";
import axios from 'axios';
import { showMessage } from "react-native-flash-message";
import { useSignin } from "./useSignIn";
import { useTranslation } from 'react-i18next';
import { useSilentToken } from "./AzureAd/useAzureADHelper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export const useAccount = () => {

    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { dispatch } = useAccountContext();
    const { isWinAuth } = useSettingsContext();
    const { refreshSignIn } = useSignin();
    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
    const { acquireSilentToken } = useSilentToken();
    const insets = useSafeAreaInsets();
    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            'accepts': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    axiosInstance.interceptors.request.use(request => {
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response, // Directly return successful responses.
        async error => {
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    if (!isWinAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axiosInstance(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    const accountEdit = async (userData) => {
        setIsLoading(true);
        setError(null);
        await axiosInstance.post(`${clientApi}api/Employee`, userData, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + user.token

            },
        })
            .then((response) => {
                if (response.status === 200) {
                    // update the auth context
                    accountGet();
                    showMessage({
                        message: t('common:detailsSaved'),
                        type: "success",
                        duration: 3000,
                        position: "top",
                        ...(Platform.OS === 'web' ? {} : { style: { marginTop: insets.top + 16 }}),
                        ...(Platform.OS === 'web' ? {}  : {floating: true,})
                    });
                };
            })
            .catch(error => {
                if (error.response && error.response.status == '403') {
                    showMessage({
                        message: "Could not update the Employee Profile",
                        type: "danger",
                        duration: 3000,
                        position: "top",
                        ...(Platform.OS === 'web' ? {} : { style: { marginTop: insets.top + 16 }}),
                        ...(Platform.OS === 'web' ? {}  : {floating: true,})
                    });
                }
                else if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                } else {
                    console.log(error.toJSON());
                };
            });
        // update loading state
        setIsLoading(false);
    };

    const accountGet = async () => {
        setIsLoading(true);
        setError(null);

        await axiosInstance.get(`${clientApi}api/Employee`, {
            params: {
                username: username
            }
        })
            .then((response) => {
                // update the account context
                response.data.map((r) => {
                    dispatch({ type: 'UPDATE_USER', payload: r })
                });
                // * Selecte Loged in Company Details
                accountCompanyGet(response.data[0].employeeId)
                // update loading state
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                }
            });
    };

    const leaveAnalysisGet = async () => {
        setIsLoading(true);
        setError(null);

        await axiosInstance.get(`${clientApi}api/Employee/PersonalLeaveBalances`, {
            params: {
                username: username
            }
        })
            .then((response) => {
                response.data.map((r) => {
                    if (r.acodeDesc?.trim().toLowerCase() === "annual leave") {
                        dispatch({ type: 'UPDATE_REMAINING_AL', payload: r.remaining })
                    }
                    // ! this might different from company to company have a look 
                    else if (r.acodeDesc?.trim().toLowerCase() === "sick leave") {
                        dispatch({ type: 'UPDATE_REMAINING_SL', payload: r.remaining })
                    }
                });
                dispatch({ type: 'UPDATE_LEAVE_ANALYSIS', payload: response.data })
                // update loading state
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                }
            });
    };

    const emergencyInformationEdit = async (emergencyInformationData) => {
        setIsLoading(true);
        setError(null);
        await axiosInstance.post(`${clientApi}api/Employee/EmergencyInformation`, emergencyInformationData,)
            .then((response) => {
                if (response.status === 200) {
                    // update the auth context
                    accountGet();
                    showMessage({
                        message: t('common:detailsSaved'),
                        type: "success",
                        duration: 3000,
                    });
                };
            })
            .catch(error => {
                if (error.response && error.response.status == '403') {
                    showMessage({
                        message: "Could not update the Emergency Information",
                        type: "danger",
                        duration: 3000,
                    });
                } else {
                    if (error.response.status === 401) {
                        refreshSignIn(user, clientApi)
                    }
                    console.log(error.toJSON());
                };
            });
        // update loading state
        setIsLoading(false);
    };

    const emergencyInformationGet = async () => {
        setIsLoading(true);
        setError(null);

        await axiosInstance.get(`${clientApi}api/Employee/EmergencyInformation`, {
            params: {
                username: username
            }
        })
            .then((response) => {
                // update emergency information context
                response.data.map((r) => {
                    dispatch({ type: 'UPDATE_EMERGENCY_INFORMATION', payload: r })
                });
                // update loading state
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                }
                console.log(error.message);
            });
    };

    const pendingActivitiesCountGet = async () => {
        setIsLoading(true);
        setError(null);

        await axiosInstance.get(`${clientApi}api/Employee/LAAPendingCount`, {
            params: {
                username: username
            }
        })
            .then((response) => {
                // update emergency information context
                response.data.map((r) => {
                    dispatch({ type: 'UPDATE_PENDING_ACTIVITIES_COUNT', payload: r })
                });
                // update loading state
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error)
                if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                }
                console.log(error.message);
            });
    };

    const managerEmployeesGet = async () => {
        setIsLoading(true);
        setError(null);

        await axiosInstance
            .get(`${clientApi}api/leaves/ManagerEmployees`,
                {
                    params: {
                        username: username
                    }
                })
            .then(response => {
                // update manager employees context
                dispatch({ type: 'UPDATE_MANAGER_EMPLOYEES', payload: response.data })
                // update loading state
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                }
                console.log(error.message);
            });
    };

    const oasisParamGet = async () => {
        setIsLoading(true);
        setError(null);

        await axiosInstance
            .get(`${clientApi}api/OasisParam`,
                {
                    params: {
                        username: username
                    }
                })
            .then(response => {
                // update oasisparam context
                dispatch({ type: 'UPDATE_OASIS_PARAM', payload: response.data })
                // update loading state
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                }
                console.log(error.message);
            });
    };

    const teamGet = async () => {
        setIsLoading(true);
        setError(null);

        await axiosInstance
            .get(`${clientApi}api/Employee/GetTeam`,
                {
                    params: {
                        username: username
                    }
                })
            .then(response => {
                // update oasisparam context
                dispatch({ type: 'UPDATE_TEAM', payload: response.data })
                // update loading state
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                }
            });
    }

    // * To get Company Detais for account. 
    // TODO For now we are retrieving company logo only but eventually we will retrieve company colors. 
    const accountCompanyGet = async (emplId) => {
        setIsLoading(true);
        setError(null);

        if (emplId == null){
            setIsLoading(false);
            return;
        }

        await axiosInstance
            .get(`${clientApi}api/Company/CompanyLogo`, {
                params: { employeeid: emplId },
            })
            .then((response) => {
                dispatch({ type: 'UPDATE_COMPANY', payload: { logo: response.data } })
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                if (error.response.status === 401) {
                    refreshSignIn(user, clientApi)
                }
                console.log(error.message);
            });
    }

    const getDocumentsAsync = async () => {
        setIsLoading(true);
        let doc = {};
        await axiosInstance.get(`${clientApi}api/Employee/Documents`, {
            params: {
                username: username
            }
        })
            .then((response) => {
                doc = response.data;
            })
            .catch((error) => {
                console.log(error.message);
            });
        setIsLoading(false);
        return doc;
    };

    const getOrgChart = async (company) => {
        setIsLoading(true);
        if (company === ""){
            setIsLoading(false);
            return;
        }
        let doc = {};
        await axiosInstance.get(`${clientApi}api/Company/GetOrgChart`, {
            params: {
                username: username,
                company: company
            }
        })
            .then((response) => {
                dispatch({ type: 'UPDATE_ORGCHART', payload: response.data })
            })
            .catch((error) => {
                console.log(error.message);
            });
        setIsLoading(false);
        return doc;
    }

    return {
        accountGet, accountEdit, emergencyInformationGet,
        emergencyInformationEdit, pendingActivitiesCountGet, leaveAnalysisGet,
        managerEmployeesGet, oasisParamGet, teamGet, getDocumentsAsync, accountCompanyGet, getOrgChart, isLoading, error
    }
}